/**
 * Ethereum Decoding Module
 *
 * Transaction decoding and pre-sign verification for EVM transactions.
 * Includes built-in Stakewise vault support.
 *
 * @example
 * ```ts
 * import { DecodingSDK } from "@blockdaemon/transaction-verifier";
 *
 * const sdk = new DecodingSDK({ logLevel: "info" });
 * const eth = sdk.ethereumDecodingModule;
 *
 * // Decode Stakewise transaction
 * const decoded = eth.decodeStakewiseTransaction(calldataHex);
 *
 * // Verify stake transaction
 * const result = eth.stakewiseVerifyStakeUnsignedTx(
 *   { assets: 1000000000000000000n, userAddress: "0x...", chainId: "1" },
 *   { to: vaultAddress, data: calldataHex, value: 1000000000000000000n },
 *   { expectedChainId: "1" }
 * );
 * ```
 *
 * @module
 */
import { isDecodingError } from "../../shared/error.js";
import { getLogger } from "../../shared/logger.js";
import { _decodeTransaction, _tryDecodeTransaction, } from "./decoding/decoder.js";
import { _addressesEqual, _computeSelector, _encodeTransaction, _getFunctionSelector, _normalizeAddress, _recoverAddress, } from "./decoding/decoder-utils.js";
import { _stakewiseVerifyClaimExitQueueUnsignedTx, _stakewiseVerifyStakeUnsignedTx, _stakewiseVerifyWithdrawUnsignedTx, _verifyGenericUnsignedTx, } from "./decoding/verification.js";
import { _getIntentHashData } from "./decoding/verification-helpers.js";
import { stakewiseVaultAbi } from "./stakewise/stakewise-interfaces.js";
/**
 * Ethereum decoding and verification module.
 *
 * Capabilities:
 * - Decode calldata using any ABI or built-in Stakewise ABI
 * - Verify Stakewise stake/withdraw/claim transactions match intent
 * - Generic transaction verification against expected parameters
 * - Address utilities and selector computation
 */
export class EthereumDecodingModule {
    logger;
    sharesTolerancePercent;
    constructor(config) {
        const logOptions = {
            label: "ethereum-decoding-module",
            level: config.logLevel,
        };
        this.logger = getLogger(logOptions);
        this.logger.addContext("module", "ethereum-decoding-module");
        this.sharesTolerancePercent = config.sharesTolerancePercent ?? 10;
    }
    // ================================
    // Transaction Decoding
    // ================================
    /** Decodes calldata using an ABI. Throws on failure. */
    decodeTransaction(data, abi) {
        this.logger.debug("decodeTransaction", { dataLength: data.length });
        return _decodeTransaction(data, abi, this.logger);
    }
    /** Decodes calldata using an ABI. Returns null on failure. */
    tryDecodeTransaction(data, abi) {
        this.logger.debug("tryDecodeTransaction", { dataLength: data.length });
        return _tryDecodeTransaction(data, abi, this.logger);
    }
    /** Decodes Stakewise vault transaction (built-in ABI). Throws on failure. */
    decodeStakewiseTransaction(data) {
        this.logger.debug("decodeStakewiseTransaction", { dataLength: data.length });
        return _decodeTransaction(data, stakewiseVaultAbi, this.logger);
    }
    /** Decodes Stakewise vault transaction (built-in ABI). Returns null on failure. */
    tryDecodeStakewiseTransaction(data) {
        this.logger.debug("tryDecodeStakewiseTransaction", { dataLength: data.length });
        return _tryDecodeTransaction(data, stakewiseVaultAbi, this.logger);
    }
    // ================================
    // Address & Selector Utilities
    // ================================
    /** Returns EIP-55 checksummed address. */
    normalizeAddress(address) {
        this.logger.debug("normalizeAddress", { address });
        return _normalizeAddress(address, this.logger);
    }
    /** Case-insensitive address comparison. */
    addressesEqual(a, b) {
        this.logger.debug("addressesEqual", { a, b });
        return _addressesEqual(a, b, this.logger);
    }
    /** Extracts 4-byte selector from calldata. */
    getFunctionSelector(data) {
        this.logger.debug("getFunctionSelector", { dataLength: data.length });
        return _getFunctionSelector(data, this.logger);
    }
    /** Computes selector from signature (e.g., "transfer(address,uint256)"). */
    computeSelector(signature) {
        this.logger.debug("computeSelector", { signature });
        return _computeSelector(signature, this.logger);
    }
    /** Encodes a function call into calldata. */
    encodeTransaction(abi, functionName, args) {
        this.logger.debug("encodeTransaction", { functionName, argsLength: args.length });
        return _encodeTransaction(abi, functionName, args, this.logger);
    }
    /** Recovers signer address from message hash and signature. */
    async recoverAddress(hash, signature) {
        this.logger.debug("recoverAddress", { hash });
        return _recoverAddress(hash, signature, this.logger);
    }
    // ================================
    // Stakewise Verification
    // ================================
    /**
     * Verifies a Stakewise staking deposit transaction.
     * Checks: target address, value matches assets, calldata decodes to "deposit".
     */
    stakewiseVerifyStakeUnsignedTx(request, tx, options) {
        this.logger.debug("stakewiseVerifyStakeUnsignedTx", { userAddress: request.userAddress });
        return _stakewiseVerifyStakeUnsignedTx(request, tx, options, this.logger);
    }
    /**
     * Verifies a Stakewise withdrawal transaction.
     * Checks: target address, calldata decodes to "enterExitQueue".
     */
    stakewiseVerifyWithdrawUnsignedTx(request, tx, options) {
        this.logger.debug("stakewiseVerifyWithdrawUnsignedTx", { userAddress: request.userAddress });
        return _stakewiseVerifyWithdrawUnsignedTx(request, tx, options, this.sharesTolerancePercent, this.logger);
    }
    /**
     * Verifies a Stakewise claim exit queue transaction.
     * Checks: target address, calldata decodes to "claimExitedAssets".
     */
    stakewiseVerifyClaimExitQueueUnsignedTx(request, tx, options) {
        this.logger.debug("stakewiseVerifyClaimExitQueueUnsignedTx", {
            userAddress: request.userAddress,
            positionsCount: request.positions.length,
        });
        return _stakewiseVerifyClaimExitQueueUnsignedTx(request, tx, options, this.logger);
    }
    // ================================
    // Generic Verification
    // ================================
    /** Verifies any unsigned EVM transaction against expected parameters. */
    verifyGenericUnsignedTx(tx, options) {
        this.logger.debug("verifyGenericUnsignedTx", { to: tx.to });
        return _verifyGenericUnsignedTx(tx, options, this.logger);
    }
    // ================================
    // Intent Hash
    // ================================
    /** Extracts selector and calldata for intent hash computation. */
    getIntentHashData(data) {
        this.logger.debug("getIntentHashData", { dataLength: data.length });
        return _getIntentHashData(data, this.logger);
    }
}
// Re-export Stakewise ABI and constants
export { stakewiseClaimExitedAssetsAbi, stakewiseClaimExitedAssetsSelector, stakewiseDepositFunctionSelector, stakewiseEnterExitQueueSelector, stakewiseMulticallAbi, stakewiseMulticallSelector, stakewiseUpdateStateSelector, stakewiseVaultAbi, } from "./stakewise/stakewise-interfaces.js";
/**
 * Type guard to check if an error is an Ethereum Staking Decoding error.
 *
 * @param error - Error to check
 * @returns True if the error is an Ethereum Staking Decoding error
 *
 * @example
 * ```typescript
 * try {
 *   const decoded = sdk.ethereumDecodingModule.decodeStakewiseTransaction(data);
 * } catch (error) {
 *   if (isEthStakingDecodingError(error)) {
 *     console.log("ETH Decoding Error:", error.message);
 *   } else {
 *     console.log("Unexpected error:", error);
 *   }
 * }
 * ```
 */
export function isEthStakingDecodingError(error) {
    if (!isDecodingError(error))
        return false;
    return error.protocol === "ethereum" || error.protocol === undefined;
}
//# sourceMappingURL=data:application/json;base64,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